import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCatalogLabels } from "@/i18n/catalog";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { PillChip } from "@/components/toggles";
import { Heart, SlidersHorizontal, Clock, X, BellPlus, Check, Search } from "lucide-react";

/**
 * MarketplacePage (iter80.9 · Batch 6 Sub-c) — konsistent zum neuen MP-Kategoriebaum.
 * - Kategorie-Chips laden `/api/marketplace/catalog/categories`.
 * - Klick auf Kategorie zeigt Sub-Kategorie-Chips.
 * - Filter-Panel für Größe / Farbe / Material / Zustand.
 * - Cards zeigen strukturierte Attribute (Größe · Farbe · Zustand).
 */
export default function MarketplacePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const cat = useCatalogLabels();

  const SORTS = [
    { value: "newest", label: t("marketplace.sort_newest") },
    { value: "price_asc", label: t("marketplace.sort_price_asc") },
    { value: "price_desc", label: t("marketplace.sort_price_desc") },
  ];

  const CONDITION_LABELS = {
    NEW: t("marketplace.condition_NEW"),
    VERY_GOOD: t("marketplace.condition_VERY_GOOD"),
    GOOD: t("marketplace.condition_GOOD"),
    CLEAN_REQUIRED: t("marketplace.condition_CLEAN_REQUIRED"),
  };
  // Backend liefert deutsche Attribut-Values (colors/materials/conditions).
  // Wir lokalisieren die angezeigten Options.
  const localizeCondOptions = (opts) =>
    (opts || []).map((c) => ({ code: c.code, label: cat.condition(c.code, c.label) }));

  const [catalog, setCatalog] = useState(null); // { tree, attributes }
  const [listings, setListings] = useState([]);
  const [selectedCat, setSelectedCat] = useState("all");
  const [selectedSubCat, setSelectedSubCat] = useState("");
  const [sort, setSort] = useState("newest");
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [filterOpen, setFilterOpen] = useState(false);
  const [fSize, setFSize] = useState("");
  const [fColor, setFColor] = useState("");
  const [fMaterial, setFMaterial] = useState("");
  const [fCondition, setFCondition] = useState("");

  // Katalog beim Mount laden
  useEffect(() => {
    (async () => {
      try {
        const [{ data: cats }, { data: attrs }] = await Promise.all([
          api.get("/marketplace/catalog/categories"),
          api.get("/marketplace/catalog/attributes"),
        ]);
        setCatalog({ tree: cats.tree || [], attributes: attrs });
      } catch { /* noop */ }
    })();
  }, []);

  const currentNode = catalog?.tree.find((n) => n.category_code === selectedCat);
  const sizeOpts = catalog && currentNode && catalog.attributes.sizes[selectedCat]
    ? catalog.attributes.sizes[selectedCat].options : [];
  const colorOpts = catalog?.attributes.colors || [];
  const materialOpts = catalog?.attributes.materials || [];
  const conditionOpts = catalog?.attributes.conditions || [];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        category: selectedCat,
        subcategory: selectedSubCat || undefined,
        size: fSize || undefined,
        color: fColor || undefined,
        material: fMaterial || undefined,
        condition: fCondition || undefined,
        sort,
      };
      const { data } = await api.get("/marketplace/feed", { params });
      setListings(data.listings || []);
      setTotal(data.total || 0);
    } catch { /* noop */ }
    finally { setLoading(false); }
  }, [selectedCat, selectedSubCat, fSize, fColor, fMaterial, fCondition, sort]);

  useEffect(() => { load(); }, [load]);

  const resetFilters = () => {
    setSelectedSubCat(""); setFSize(""); setFColor(""); setFMaterial(""); setFCondition("");
  };

  const activeFilterCount = [selectedSubCat, fSize, fColor, fMaterial, fCondition].filter(Boolean).length;

  // iter88.2: Search-Alert speichern
  const [savingAlert, setSavingAlert] = useState(false);
  const [alertSaved, setAlertSaved] = useState(false);
  const saveSearchAlert = async () => {
    if (!user || user === false) { navigate("/login"); return; }
    if (activeFilterCount === 0 && selectedCat === "all") return;
    setSavingAlert(true);
    try {
      await api.post("/search-alerts", {
        area_scope: "MARKETPLACE",
        category: selectedCat === "all" ? null : selectedCat,
        subcategory: selectedSubCat || null,
        size: fSize || null,
        color: fColor || null,
        material: fMaterial || null,
        condition: fCondition || null,
      });
      setAlertSaved(true);
      setTimeout(() => setAlertSaved(false), 3000);
    } catch { /* noop */ }
    finally { setSavingAlert(false); }
  };

  const toggleWatch = async (listingId, e) => {
    e.stopPropagation();
    if (!user || user === false) { navigate("/login"); return; }
    try {
      const { data } = await api.post(`/listings/${listingId}/watch`);
      setListings(prev => prev.map(l => l.listing_id === listingId ? { ...l, is_watched: data.watched } : l));
    } catch { /* ignore */ }
  };

  return (
    <div data-testid="marketplace-page" className="bg-white min-h-full">
      <div className="w-full max-w-7xl 2xl:max-w-full mx-auto">
      <div className="px-4 pt-4 pb-2">
        <h1 className="text-lg font-bold text-[#0A0A0A] tracking-tight">{t("marketplace.title")}</h1>
        <p className="text-xs text-[#999] mt-0.5">{t("marketplace.items_count", { count: total })}</p>
      </div>

      {/* Kategorie-Chips */}
      <div data-testid="mp-category-bar" className="flex flex-wrap gap-2 px-4 py-2">
        <PillChip
          testId="mp-cat-all"
          active={selectedCat === "all"}
          onClick={() => { setSelectedCat("all"); setSelectedSubCat(""); }}
          variant="neutral"
          size="md"
        >
          {t("common.all")}
        </PillChip>
        {(catalog?.tree || []).map((node) => (
          <PillChip
            key={node.category_code}
            testId={`mp-cat-${node.category_code}`}
            active={selectedCat === node.category_code}
            onClick={() => { setSelectedCat(node.category_code); setSelectedSubCat(""); }}
            variant="neutral"
            size="md"
          >
            {cat.mpCat(node.category_code, node.label)}
          </PillChip>
        ))}
      </div>

      {/* Sub-Kategorie-Chips */}
      {currentNode && (
        <div data-testid="mp-subcat-bar" className="flex flex-wrap gap-1.5 px-4 pb-2">
          {currentNode.subcategories.map((s) => (
            <PillChip
              key={s.code}
              testId={`mp-subcat-${s.code}`}
              active={selectedSubCat === s.code}
              onClick={() => setSelectedSubCat(selectedSubCat === s.code ? "" : s.code)}
              variant="muted"
              size="sm"
            >
              {cat.mpSub(currentNode.category_code, s.code, s.label)}
            </PillChip>
          ))}
        </div>
      )}

      {/* iter172 · Desktop-Inline-Suchfeld (unter Kategorien) */}
      <div data-testid="mp-desktop-search" className="hidden md:block px-4 pb-2">
        <div className="relative max-w-lg">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#999] pointer-events-none" />
          <input
            data-testid="mp-desktop-search-input"
            type="search"
            placeholder={t("marketplace.search_ph", { defaultValue: "Artikel im Marketplace suchen…" })}
            onKeyDown={(e) => {
              if (e.key === "Enter" && e.currentTarget.value.trim()) {
                navigate(`/search?scope=MARKETPLACE&q=${encodeURIComponent(e.currentTarget.value.trim())}`);
              }
            }}
            className="w-full h-10 pl-9 pr-3 rounded-full border border-[#E5E5E5] text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
          />
        </div>
      </div>

      {/* Sort + Filter-Toggle */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-[#E5E5E5] gap-2">
        <div className="flex gap-1.5 flex-wrap">
          {SORTS.map((s) => (
            <PillChip
              key={s.value}
              testId={`mp-sort-${s.value}`}
              active={sort === s.value}
              onClick={() => setSort(s.value)}
              variant="neutral"
              size="sm"
            >
              {s.label}
            </PillChip>
          ))}
        </div>
        <PillChip
          testId="mp-filter-toggle"
          active={activeFilterCount > 0}
          onClick={() => setFilterOpen(!filterOpen)}
          variant="neutral"
          size="sm"
          icon={<SlidersHorizontal size={12} />}
        >
          {t("marketplace.filter")}{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
        </PillChip>
      </div>

      {/* Filter-Panel */}
      {filterOpen && (
        <div data-testid="mp-filter-panel" className="px-4 py-3 bg-[#FAFAFA] border-b border-[#E5E5E5] space-y-2">
          {sizeOpts.length > 0 && (
            <FilterRow testid="mp-filter-size" label={t("marketplace.filter_size")} value={fSize} setValue={setFSize} options={sizeOpts} allLabel={t("common.all")} />
          )}
          {colorOpts.length > 0 && (
            <FilterRow testid="mp-filter-color" label={t("marketplace.filter_color")} value={fColor} setValue={setFColor}
              options={colorOpts.map((c) => ({ v: c, l: cat.color(c) }))} allLabel={t("common.all")} />
          )}
          {materialOpts.length > 0 && (
            <FilterRow testid="mp-filter-material" label={t("marketplace.filter_material")} value={fMaterial} setValue={setFMaterial}
              options={materialOpts.map((m) => ({ v: m, l: cat.material(m) }))} allLabel={t("common.all")} />
          )}
          {conditionOpts.length > 0 && (
            <FilterRow testid="mp-filter-condition" label={t("marketplace.filter_condition")}
              value={fCondition} setValue={setFCondition}
              options={localizeCondOptions(conditionOpts).map((c) => ({ v: c.code, l: c.label }))} allLabel={t("common.all")} />
          )}
          {activeFilterCount > 0 && (
            <button data-testid="mp-filter-reset" onClick={resetFilters}
              className="mt-1 flex items-center gap-1 text-[10px] text-red-500">
              <X size={10} /> {t("marketplace.filter_reset")}
            </button>
          )}
          {/* iter88.2: Search-Alert speichern */}
          {(activeFilterCount > 0 || selectedCat !== "all") && (
            <button data-testid="mp-save-alert"
              onClick={saveSearchAlert}
              disabled={savingAlert || alertSaved}
              className={`mt-2 flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-full border ${
                alertSaved ? "border-green-500 text-green-600" : "border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white"
              } transition-colors disabled:opacity-40`}
              title={t("marketplace.save_search_title")}>
              {alertSaved ? <><Check size={12} /> {t("marketplace.save_search_saved")}</> : <><BellPlus size={12} /> {savingAlert ? t("marketplace.save_search_saving") : t("marketplace.save_search")}</>}
            </button>
          )}
        </div>
      )}

      {/* Grid */}
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-6 h-6 border-2 border-[#E5E5E5] border-t-[#0A0A0A] rounded-full animate-spin" />
        </div>
      ) : listings.length === 0 ? (
        <div className="px-4 py-16 text-center text-sm text-[#999]">
          {t("marketplace.no_items")}
        </div>
      ) : (
        <div data-testid="mp-listing-grid" className="grid gap-3 p-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))" }}>
          {listings.map((listing) => (
            <div key={listing.listing_id} data-testid={`mp-listing-${listing.listing_id}`}
              onClick={() => navigate(`/marketplace/${listing.listing_id}`)}
              className="bg-white flex flex-col text-left group cursor-pointer rounded-2xl overflow-hidden border border-[#E5E5E5] hover:border-[#0A0A0A] transition-colors">
              <div className="relative aspect-[3/4] bg-[#F5F5F5] overflow-hidden">
                {listing.media_urls?.[0] ? (
                  <img src={listing.media_urls[0]} alt="" className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[#CCC] text-xs">{t("common.no_image")}</div>
                )}
                {(listing.media_urls?.length || 0) > 1 && (
                  <span data-testid={`mp-img-count-${listing.listing_id}`}
                    className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-black/60 backdrop-blur-sm text-white text-[10px] font-medium">
                    +{listing.media_urls.length - 1}
                  </span>
                )}
                <button data-testid={`mp-watch-${listing.listing_id}`}
                  onClick={(e) => toggleWatch(listing.listing_id, e)}
                  className="absolute top-2 right-2 p-1.5 rounded-full bg-white/80 backdrop-blur-sm transition-colors hover:bg-white">
                  <Heart size={16} className={listing.is_watched ? "fill-[#0A0A0A] text-[#0A0A0A]" : "text-[#666]"} />
                </button>
              </div>
              <div className="p-2 space-y-1">
                <p className="text-xs font-medium text-[#0A0A0A] line-clamp-2 leading-tight min-h-[2rem]">{listing.title}</p>
                {/* iter80.9: Strukturierte Attribute in Card */}
                <div className="flex flex-wrap gap-1">
                  {listing.size && <Chip>{listing.size === "Einheitsgröße" ? cat.onesize() : listing.size}</Chip>}
                  {listing.color && <Chip>{cat.color(listing.color)}</Chip>}
                  {listing.condition_code && <Chip>{cat.conditionShort(listing.condition_code, CONDITION_LABELS[listing.condition_code] || listing.condition_code)}</Chip>}
                </div>
                <div className="flex items-center justify-between mt-1">
                  <p className="text-sm font-bold text-[#0A0A0A]">{listing.price?.toFixed(2)} {listing.currency}</p>
                  {Number(listing.effective_processing_delay_days) > 0 && (
                    <span data-testid={`mp-listing-delay-${listing.listing_id}`}
                      title={t("marketplace.processing_delay_title", { days: listing.effective_processing_delay_days })}
                      className="inline-flex items-center gap-0.5 text-[9px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                      <Clock size={9} />+{listing.effective_processing_delay_days}d
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      </div>
    </div>
  );
}

function Chip({ children }) {
  return <span className="inline-block px-2 py-0.5 rounded-full bg-[#F5F5F5] text-[9px] text-[#666]">{children}</span>;
}

function FilterRow({ label, value, setValue, options, testid, allLabel }) {
  const isObj = options.length > 0 && typeof options[0] === "object";
  return (
    <div className="flex items-center gap-2">
      <label className="text-[10px] text-[#666] w-16 shrink-0">{label}</label>
      <select data-testid={testid} value={value} onChange={(e) => setValue(e.target.value)}
        className="flex-1 h-8 px-3 rounded-full border border-[#E5E5E5] text-[11px] bg-white">
        <option value="">{allLabel}</option>
        {options.map((o) => isObj ? (
          <option key={o.v} value={o.v}>{o.l}</option>
        ) : (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    </div>
  );
}
